import React, { Component } from 'react';
import { Button } from '../ui/Button';
import { AlertCircleIcon, XCircleIcon, WifiOffIcon, ClockIcon, ShieldAlertIcon } from 'lucide-react';
export type ErrorCode = 'AUTH_FAILED' | 'CONSENT_DENIED' | 'BANK_UNAVAILABLE' | 'SESSION_EXPIRED' | 'NETWORK_ERROR' | 'UNKNOWN_ERROR';
export interface ErrorScreenProps {
  errorCode: ErrorCode;
  errorMessage?: string;
  requestId?: string;
  onRetry?: () => void;
  onCancel: () => void;
}
const errorConfig: Record<ErrorCode, {
  title: string;
  message: string;
  icon: ComponentType<{
    className?: string;
  }>;
  iconColor: string;
  iconBg: string;
  action: string;
  recoverable: boolean;
}> = {
  AUTH_FAILED: {
    title: 'Authentication Failed',
    message: 'The credentials you entered were incorrect. Please check your username and password and try again.',
    icon: XCircleIcon,
    iconColor: 'text-red-600',
    iconBg: 'bg-red-100',
    action: 'Try Again',
    recoverable: true
  },
  CONSENT_DENIED: {
    title: 'Consent Declined',
    message: "You declined to share your data. You can try again anytime when you're ready.",
    icon: ShieldAlertIcon,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-100',
    action: 'Go Back',
    recoverable: true
  },
  BANK_UNAVAILABLE: {
    title: 'Bank System Unavailable',
    message: "The bank's system is temporarily unavailable. This is usually resolved quickly. Please try again in a few minutes.",
    icon: WifiOffIcon,
    iconColor: 'text-orange-600',
    iconBg: 'bg-orange-100',
    action: 'Retry',
    recoverable: true
  },
  SESSION_EXPIRED: {
    title: 'Session Expired',
    message: 'Your session has expired for security reasons. Please start the consent process again.',
    icon: ClockIcon,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
    action: 'Start Over',
    recoverable: true
  },
  NETWORK_ERROR: {
    title: 'Connection Problem',
    message: "We couldn't connect to the server. Please check your internet connection and try again.",
    icon: WifiOffIcon,
    iconColor: 'text-gray-600',
    iconBg: 'bg-gray-100',
    action: 'Retry',
    recoverable: true
  },
  UNKNOWN_ERROR: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again or contact support if this problem persists.',
    icon: AlertCircleIcon,
    iconColor: 'text-red-600',
    iconBg: 'bg-red-100',
    action: 'Try Again',
    recoverable: false
  }
};
export function ErrorScreen({
  errorCode,
  errorMessage,
  requestId,
  onRetry,
  onCancel
}: ErrorScreenProps) {
  const config = errorConfig[errorCode];
  const Icon = config.icon;
  return <div className="space-y-6 text-center" data-id="element-784">
      {/* Error Icon */}
      <div className={`w-20 h-20 rounded-full ${config.iconBg} mx-auto flex items-center justify-center`} data-id="element-785">
        <Icon className={`w-10 h-10 ${config.iconColor}`} data-id="element-786" />
      </div>

      {/* Error Message */}
      <div data-id="element-787">
        <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-788">{config.title}</h2>
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-789">
          {errorMessage || config.message}
        </p>
      </div>

      {/* Technical Details (Collapsible) */}
      {requestId && <details className="text-left bg-gray-50 rounded-xl overflow-hidden" data-id="element-790">
          <summary className="px-4 py-3 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors" data-id="element-791">
            Technical Details (for support)
          </summary>
          <div className="px-4 py-3 border-t border-gray-200 space-y-1" data-id="element-792">
            <div className="flex justify-between text-xs" data-id="element-793">
              <span className="text-gray-500" data-id="element-794">Error Code:</span>
              <span className="font-mono text-gray-900" data-id="element-795">{errorCode}</span>
            </div>
            <div className="flex justify-between text-xs" data-id="element-796">
              <span className="text-gray-500" data-id="element-797">Request ID:</span>
              <span className="font-mono text-gray-900" data-id="element-798">{requestId}</span>
            </div>
            <div className="flex justify-between text-xs" data-id="element-799">
              <span className="text-gray-500" data-id="element-800">Timestamp:</span>
              <span className="font-mono text-gray-900" data-id="element-801">
                {new Date().toISOString()}
              </span>
            </div>
          </div>
        </details>}

      {/* Action Buttons */}
      <div className="space-y-3 pt-2" data-id="element-802">
        {config.recoverable && onRetry && <Button onClick={onRetry} className="w-full" data-id="element-803">
            {config.action}
          </Button>}
        <Button onClick={onCancel} variant="outline" className="w-full" data-id="element-804">
          {config.recoverable ? 'Cancel' : 'Go Back'}
        </Button>
      </div>

      {/* Support Link */}
      <div className="text-xs text-gray-500" data-id="element-805">
        Need help?{' '}
        <a href="#" className="text-blue-600 hover:text-blue-700 font-medium underline" data-id="element-806">
          Contact Support
        </a>
      </div>

      {/* Security Notice for Auth Failures */}
      {errorCode === 'AUTH_FAILED' && <div className="bg-amber-50 border-2 border-amber-200 p-4 rounded-xl text-left" data-id="element-807">
          <div className="flex items-start gap-3" data-id="element-808">
            <ShieldAlertIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-809" />
            <div className="text-xs text-amber-900" data-id="element-810">
              <div className="font-semibold mb-1" data-id="element-811">Security Notice</div>
              <div data-id="element-812">
                Multiple failed login attempts may temporarily lock your account
                for security. If you've forgotten your password, use the "Forgot
                Password" option.
              </div>
            </div>
          </div>
        </div>}
    </div>;
}